import { create } from 'zustand';
import { apiErrorMessage } from '@/utils/apiError';
import axios from '@/utils/axios';

/**
 * Whether a notification counts on the BELL. A chat message has its own
 * badge (MessageBell) and the server's /notifications/unread-count already
 * leaves it out, so the bell must too.
 *
 * `unreadCount` below means exactly that number. It used to be counted from
 * the newest page of the list with messages filtered out, so a member whose
 * newest 50 notifications were all chat messages saw "no new notifications"
 * while the server held unread ones for her (BUG-521).
 */
export const countsOnBell = (n) => Boolean(n) && n.type !== 'new_message';
const unreadOnBell = (n) => countsOnBell(n) && !n.is_read;


/**
 * Global Notifications Store using Zustand
 *
 * This store provides:
 * - Centralized state for notifications
 * - Real-time updates from socket events
 * - Unread count management
 */
const useNotificationsStore = create((set, get) => ({
  // State
  notifications: [],
  unreadCount: 0,
  loading: false,
  lastFetch: null,
  error: null,

  // Actions

  /**
   * Fetch all notifications from API
   */
  fetchNotifications: async () => {
    const { loading, lastFetch } = get();

    // Debounce: Don't fetch if last fetch was less than 2 seconds ago
    if (loading || (lastFetch && Date.now() - lastFetch < 2000)) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await axios.get(`/notifications`);

      const notifications = response.data.notifications || [];

      set({
        notifications,
        // From the page until the server's count arrives; the server's count
        // covers notifications older than this page, the page cannot.
        unreadCount: notifications.filter(unreadOnBell).length,
        loading: false,
        lastFetch: Date.now()
      });
      get().fetchUnreadCount();
    } catch (error) {
      set({
        loading: false,
        error: apiErrorMessage(error, 'Failed to fetch notifications')
      });
    }
  },

  /**
   * Fetch only unread count (lighter than full fetch)
   */
  fetchUnreadCount: async () => {
    try {
      const response = await axios.get(`/notifications/unread-count`);

      const count = response.data?.unread_count;
      if (typeof count === 'number') set({ unreadCount: count });
    } catch (error) {
      // Keep the count from the page: one missed refresh is not worth a
      // badge that says zero.
    }
  },

  /**
   * Handle new notification from socket
   */
  handleNewNotification: (notification) => {
    const { notifications, unreadCount } = get();

    // Check if notification already exists
    if (notifications.some(n => n.id === notification.id)) {
      return;
    }

    // Add to top of list
    set({
      notifications: [notification, ...notifications],
      unreadCount: unreadOnBell(notification) ? unreadCount + 1 : unreadCount
    });
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId) => {
    const { notifications, unreadCount } = get();
    const target = notifications.find(n => n.id === notificationId);

    // Optimistic update
    set({
      notifications: notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ),
      unreadCount: unreadOnBell(target) ? Math.max(0, unreadCount - 1) : unreadCount
    });

    // API call
    try {
      await axios.put(`/notifications/${notificationId}/read`, {});
    } catch (error) {
      // Revert on error
      set({
        notifications: notifications,
        unreadCount: unreadCount
      });
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    const { notifications } = get();

    // Optimistic update
    set({
      notifications: notifications.map(n => ({ ...n, is_read: true })),
      unreadCount: 0
    });

    // API call - correct endpoint is mark-all-read
    try {
      await axios.put(`/notifications/mark-all-read`, {});
    } catch (error) {
      // Revert on error - refetch
      const { fetchNotifications } = get();
      fetchNotifications();
    }
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId) => {
    const { notifications, unreadCount } = get();
    const notification = notifications.find(n => n.id === notificationId);

    // Optimistic update
    set({
      notifications: notifications.filter(n => n.id !== notificationId),
      unreadCount: unreadOnBell(notification)
        ? Math.max(0, unreadCount - 1)
        : unreadCount
    });

    // API call
    try {
      await axios.delete(`/notifications/${notificationId}`);
    } catch (error) {
      // Revert on error
      set({
        notifications,
        unreadCount
      });
    }
  },

  /**
   * Reset store (on logout)
   */
  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      loading: false,
      lastFetch: null,
      error: null
    });
  }
}));

export default useNotificationsStore;
